import cx from "classnames";
import React from "react";
import ReactDOM from "react-dom";
import { compose } from "redux";
import { connect } from "react-redux";
import {
  AccountType,
  BreakdownClosedEarlyLabel,
  ExpandableRow,
  getErrorPopupTexts,
  IconTextLink,
  PageHeader,
} from "component-library";
import { withTranslation, WithTranslation } from "i18n";
import { CastingTypes } from "shared-types";
import {
  LaunchDarklyClientWithFlags,
  withLaunchDarklyConsumerHOC,
  FEATURE_FLAGS,
} from "shared-services";
import Breakdown from "shared/components/Breakdown/Breakdown";
import {
  prepareAttachmentLink,
  prepareAttachmentTarget,
} from "shared/helpers/breakdownViewHelpers";
import { findFacetName } from "shared/helpers/facetHelpers";
import { IBreakdown, ILinkAttachment } from "shared/models/breakdown";
import { IFacetGroup } from "shared/models/facet";
import { AccessType, IUser } from "shared/models/user";
import scrollManager from "shared/services/scrollManager";
import { MapDispatchToProps } from "shared/types/utilities";

import config from "config/global";

import { IApplicationState } from "store";
import { FacetStoreKey } from "store/facet/facet.types";
import { reportConcern } from "store/job/job.actions";
import { ReportConcern } from "store/job/job.types";
import { ITranslationState } from "store/translation/translation.types";
import { hidePopup, showPopup } from "store/ui/ui.actions";
import { HidePopup, PopupType, ShowPopup } from "store/ui/ui.types";

import getErrorPopupType from "helpers/getErrorPopupType";
import dateService from "services/dateService";

import { prepareReportConcernPopupTexts } from "components/Popups/ReportConcernPopup/ReportConcernPopup.helpers";
import SuggestionsViewAgent, {
  ISuggestionsViewAgentLoadedData,
} from "components/SuggestionsViewAgent/SuggestionsViewAgent";
import SuggestionsViewPerformer, {
  ISuggestionsViewPerformerLoadedData,
} from "components/SuggestionsViewPerformer/SuggestionsViewPerformer";

import "./BreakdownView.scss";
import { IReportConcernPopupModel } from "components/Popups/ReportConcernPopup/ReportConcernPopup.model";

export interface IBreakdownViewRegisterRole {
  id: string;
  callback(
    loadedData:
      | ISuggestionsViewAgentLoadedData
      | ISuggestionsViewPerformerLoadedData
  ): void;
}

export interface IBreakdownViewOnDataLoaded {
  id: string;
  loadedData:
    | ISuggestionsViewAgentLoadedData
    | ISuggestionsViewPerformerLoadedData;
}

export interface IBreakdownViewRole {
  id: string;
  title: string;
  description?: string;
  playingAge?: string;
  height: string;
  appearance: string;
  gender: string;
  disability: string;
  contractType: string;
  contractTypeInfo: string;
  linkAttachments: ILinkAttachment[];
  genderIds: number[];
  appearanceIds: number[];
  isCast?: boolean | null;
  anyEthnicity: boolean;
  ethnicityLabel: string;
}

export interface IBreakdownViewTexts {
  title: string;
  sent: string;
  closingDate: string;
  rolesSectionTitle: string;
  productionLocation: string;
  castingLocation: string;
  expander?: {
    expandAll: string;
    collapseAll: string;
  };
  attachmentsLabel: string;
  projectSummaryLabel: string;
  roleDisabilityLabel: string;
  roleGenderLabel: string;
  castingDetailsLabel: string;
  rolePlayingAgeLabel: string;
  roleHeightLabel: string;
  roleDisabilityInfoLabel: string;
  productionDatesLabel: string;
  roleAnyHeight: string;
  roleAppearanceLabel: string;
  castingDetailsSectionLabel: string;
  rolesLabel: string;
  roleContractTypeInfoLabel: string;
  roleAnyAges: string;
  castingLocationLabel: string;
  productionLocationLabel: string;
  productionDetailsSectionLabel: string;
  sentByFormat: string;
  roleAttachmentsLabel: string;
  castingDirectorLabel: string;
  sentByAnonymous: string;
  roleContractTypeLabel: string;
  message: string;
  productionProducerLabel: string;
  projectOverviewLabel: string;
  productionDirectorLabel: string;
  productionCompanyLabel: string;
  productionContractTypeLabel: string;
  exclusiveText: string;
  exclusiveTitle: string;
  productionContractType: string;
  termsLabel: string;
  termsText: string;
  reportConcernButton: string;
  hiringPreferenceLabel: string;
  localHireIsRequired: string;
  localHireDescription: string;
  castingTypeLabel: string;
  castingTypeOnline: string;
  castingTypeSelfTape: string;
  castingTypeInPerson: string;
  localHireNoPreferences: string;
  isRoleCastLabelText: string;
}

export interface IBreakdownViewActions {
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  reportConcern: ReportConcern;
}

export interface IBreakdownViewStoreProps {
  translation: ITranslationState;
  complaintCategories: IFacetGroup[];
  isPerformer?: boolean;
}

interface IBreakdownViewComposeProps {
  breakdown: IBreakdown;
  roles: IBreakdownViewRole[];
  texts: IBreakdownViewTexts;
  actionList?: React.JSX.Element;
  limitWidth?: boolean;
  isPrintView?: boolean;
  user: IUser;
}

export interface IBreakdownViewProps
  extends IBreakdownViewActions,
    IBreakdownViewStoreProps,
    IBreakdownViewComposeProps,
    LaunchDarklyClientWithFlags,
    WithTranslation {}

export interface IBreakdownViewState {
  currentEditingRole?: IBreakdownViewRole;
}

export class BreakdownView extends React.Component<
  IBreakdownViewProps,
  IBreakdownViewState
> {
  private rolesRegistry: Map<
    string,
    (
      data:
        | ISuggestionsViewAgentLoadedData
        | ISuggestionsViewPerformerLoadedData
    ) => void
  > = new Map();
  public rolesRowRefs: (ExpandableRow | null)[] = [];
  public readonly state: Readonly<IBreakdownViewState> = {};

  public render() {
    this.rolesRowRefs = [];

    return (
      <article
        className={cx("c-breakdown-view", {
          "c-breakdown-view--print": this.props.isPrintView,
        })}
      >
        {this.renderContent()}
        {this.renderPrintArea()}
      </article>
    );
  }

  public renderContent() {
    const { texts, breakdown, isPrintView } = this.props;

    if (isPrintView) {
      return null;
    }

    return (
      <>
        <PageHeader
          pageTitle={
            <Breakdown.Title
              title={texts.title}
              sentBy={texts.sent}
              closingDate={
                breakdown?.closedEarly ? (
                  <BreakdownClosedEarlyLabel
                    reason={breakdown.closedReason}
                    closeDate={breakdown.castingClosingDate}
                  />
                ) : (
                  texts.closingDate
                )
              }
              exclusive={breakdown.spotlightExclusive}
              exclusiveText={texts.exclusiveText}
              exclusiveTitle={texts.exclusiveTitle}
            />
          }
          actionSection={this.props.actionList}
        />
        <Breakdown.Content limitWidth={this.props.limitWidth}>
          {breakdown.message && (
            <Breakdown.Message keepFormatting={true}>
              {breakdown.message}
            </Breakdown.Message>
          )}
          <Breakdown.ExpandableContainer>
            <ExpandableRow
              header={texts.projectOverviewLabel}
              toggleAriaLabel={texts.projectOverviewLabel}
              isInitiallyExpanded={true}
            >
              <Breakdown.Section>
                <Breakdown.Row>
                  <Breakdown.Column spanColumns={8}>
                    {this.renderSummarySection()}
                    {this.renderProductionDetailsSection()}
                    {this.renderCastingDetailsSection()}
                  </Breakdown.Column>
                  {this.renderAttachmentsColumn()}
                </Breakdown.Row>
              </Breakdown.Section>
            </ExpandableRow>
          </Breakdown.ExpandableContainer>
          <Breakdown.SectionTitle
            aside={
              texts.expander ? (
                <span className="flex gap-1">
                  <a
                    className="g-alternative-link g-alternative-link--lg"
                    onClick={this.expandRoles}
                    tabIndex={0}
                  >
                    {texts.expander.expandAll}
                  </a>
                  {" | "}
                  <a
                    className="g-alternative-link g-alternative-link--lg"
                    onClick={this.collapseRoles}
                    tabIndex={0}
                  >
                    {texts.expander.collapseAll}
                  </a>
                </span>
              ) : undefined
            }
          >
            {texts.rolesSectionTitle}
          </Breakdown.SectionTitle>
          {this.props.roles.map(this.renderRole, this)}
          <Breakdown.Message>
            <Breakdown.Text>
              <span dangerouslySetInnerHTML={{ __html: texts.message }} />
            </Breakdown.Text>
          </Breakdown.Message>
        </Breakdown.Content>
      </>
    );
  }

  public renderSummarySection(isPrintView?: boolean) {
    const { texts, breakdown } = this.props;

    if (!breakdown.production.summary) {
      return null;
    }

    return (
      <>
        <Breakdown.SectionTitle isPrintView={isPrintView}>
          {texts.projectSummaryLabel}
        </Breakdown.SectionTitle>
        <Breakdown.Text keepFormatting={true} isPrintView={isPrintView}>
          {breakdown.production.summary}
        </Breakdown.Text>
      </>
    );
  }

  public renderProductionDetailsSection(isPrintView?: boolean) {
    const { texts, breakdown } = this.props;
    const hasFullAccess = this.hasFullAccess();

    return (
      <>
        <Breakdown.SectionTitle isPrintView={isPrintView}>
          {texts.productionDetailsSectionLabel}
        </Breakdown.SectionTitle>
        <Breakdown.InfoTable>
          {breakdown.production.dates && (
            <Breakdown.InfoTable.Row
              title={texts.productionDatesLabel}
              content={breakdown.production.dates}
              keepFormatting={true}
              isPrintView={isPrintView}
            />
          )}
          <Breakdown.InfoTable.Row
            title={texts.productionLocationLabel}
            content={breakdown?.production?.location}
            isPrintView={isPrintView}
          />
          {hasFullAccess && breakdown.production.director && (
            <Breakdown.InfoTable.Row
              title={texts.productionDirectorLabel}
              content={breakdown.production.director}
              isPrintView={isPrintView}
            />
          )}
          {hasFullAccess && breakdown.production.producer && (
            <Breakdown.InfoTable.Row
              title={texts.productionProducerLabel}
              content={breakdown.production.producer}
              isPrintView={isPrintView}
            />
          )}
          <Breakdown.InfoTable.Row
            title={texts.productionContractTypeLabel}
            content={texts.productionContractType}
            isPrintView={isPrintView}
          />
          {hasFullAccess && breakdown.production.company && (
            <Breakdown.InfoTable.Row
              title={texts.productionCompanyLabel}
              content={breakdown.production.company}
              isPrintView={isPrintView}
            />
          )}
        </Breakdown.InfoTable>
      </>
    );
  }

  public renderCastingDetailsSection(isPrintView?: boolean) {
    const { texts, breakdown, t } = this.props;
    const { castingTypes = [], isLocalHire } = breakdown;
    const castingTypesValue = castingTypes
      .map((value) => {
        const castingTypeTexts = {
          1: texts.castingTypeOnline,
          2: texts.castingTypeSelfTape,
          3: texts.castingTypeInPerson,
          4: t("common:breakdown.castingType.other"),
        };

        switch (String(value)) {
          case CastingTypes.InPerson:
            return `${castingTypeTexts[value]} (${breakdown.castingLocations
              .map(({ name }) => name)
              .join(", ")})`;
          case CastingTypes.Other:
            return `${castingTypeTexts[value]} (${breakdown.castingTypeOtherDetails})`;
          default:
            return castingTypeTexts[value];
        }
      })
      .join(", ");

    if (!this.hasFullAccess()) {
      return null;
    }

    return (
      <>
        <Breakdown.SectionTitle isPrintView={isPrintView}>
          {texts.castingDetailsSectionLabel}
        </Breakdown.SectionTitle>
        <Breakdown.InfoTable>
          {breakdown.production.castingDirector && (
            <Breakdown.InfoTable.Row
              title={texts.castingDirectorLabel}
              content={breakdown.production.castingDirector}
              isPrintView={isPrintView}
            />
          )}
          <Breakdown.InfoTable.Row
            title={texts.castingTypeLabel}
            content={castingTypesValue}
            isPrintView={isPrintView}
          />
          <Breakdown.InfoTable.Row
            title={texts.hiringPreferenceLabel}
            content={
              isLocalHire
                ? texts.localHireIsRequired
                : texts.localHireNoPreferences
            }
            isPrintView={isPrintView}
          />
          {breakdown.castingDetails && (
            <Breakdown.InfoTable.Row
              title={texts.castingDetailsLabel}
              content={breakdown.castingDetails}
              keepFormatting={true}
              isPrintView={isPrintView}
            />
          )}
        </Breakdown.InfoTable>
      </>
    );
  }

  public renderTermsMessage(isPrintView?: boolean) {
    const { texts, breakdown } = this.props;
    const { isLocalHire } = breakdown;

    return (
      <Breakdown.Message isPrintView={isPrintView}>
        <Breakdown.ImportantMessage
          title={texts.termsLabel}
          content={texts.termsText}
          isPrintView={isPrintView}
        />
        {isLocalHire && (
          <Breakdown.ImportantMessage
            title={texts.localHireIsRequired}
            content={texts.localHireDescription}
            isPrintView={isPrintView}
          />
        )}
      </Breakdown.Message>
    );
  }

  public renderAttachment(attachment: ILinkAttachment) {
    return (
      <IconTextLink
        key={attachment.attachmentId}
        text={attachment.originalFileName}
        linkUrl={prepareAttachmentLink(attachment.attachmentId)}
        iconName="icon-paperclip1"
        variant="primary"
        target={prepareAttachmentTarget(attachment.originalFileName)}
      />
    );
  }

  public renderAttachmentsColumn() {
    const { texts, breakdown } = this.props;

    return (
      <Breakdown.Column spanColumns={4}>
        {breakdown.linkAttachments.length > 0 && (
          <>
            <Breakdown.SectionTitle>
              {texts.attachmentsLabel}
            </Breakdown.SectionTitle>
            {breakdown.linkAttachments.map(this.renderAttachment)}
          </>
        )}
        {this.renderTermsMessage()}
      </Breakdown.Column>
    );
  }

  public renderRole(
    role: IBreakdownViewRole,
    index: number,
    allRoles: IBreakdownViewRole[]
  ) {
    const isTransitionDisabled =
      allRoles.length >= config.settings.rolesCountTransitionDisabledThreshold;
    const { texts } = this.props;

    return (
      <Breakdown.ExpandableContainer key={role.id}>
        <ExpandableRow
          header={role.title}
          toggleAriaLabel={role.title}
          isInitiallyExpanded={true}
          uiFlag={{
            text: texts.isRoleCastLabelText,
            isVisible: !!role.isCast,
          }}
          ref={(ref) => (this.rolesRowRefs[index] = ref)}
          isTransitionDisabled={isTransitionDisabled}
        >
          {this.renderRoleContent(role, index)}
        </ExpandableRow>
      </Breakdown.ExpandableContainer>
    );
  }

  private renderRoleContent(
    role: IBreakdownViewRole,
    index: number,
    isPrintView?: boolean
  ) {
    const { texts, user, breakdown, flags, t } = this.props;
    const { currentEditingRole } = this.state;
    const isRoleClosed = this.hasAccessForSuggestion();
    const isEditMode =
      !isPrintView && currentEditingRole
        ? currentEditingRole.id === role.id
        : false;
    const isBuiltInPrintView = !this.props.isPrintView && isPrintView;
    const reportConcernButton = (
      <IconTextLink
        text={texts.reportConcernButton}
        iconName="icon-alarm"
        variant="primary"
        handleClick={this.handleReportConcern(role.id)}
      />
    );
    const suggestionViewBaseProps = {
      breakdown,
      role,
      isRoleClosed,
      reportConcernButton,
      registerRole: isBuiltInPrintView ? this.registerRole : undefined,
      onDataLoaded: isBuiltInPrintView ? undefined : this.handleDataLoaded,
      isRoleCast: role.isCast,
    };
    const suggestionsViewPerformerProps = {
      ...suggestionViewBaseProps,
      userAccessType: user.performerAccessType!,
    };
    const suggestionsViewAgentProps = {
      ...suggestionViewBaseProps,
      isPrintView,
      isEditMode,
      location:
        breakdown.isLocalHire && breakdown.production.locationId
          ? [
              {
                name: breakdown.production.location,
                value: breakdown.production.locationId,
              },
            ]
          : [],
      disableEdit: currentEditingRole !== undefined,
      switchEditMode: this.switchEditMode(role, index),
    };

    return (
      <>
        <Breakdown.Section isPrintView={isPrintView}>
          {role.description && (
            <Breakdown.Text keepFormatting={true} isPrintView={isPrintView}>
              {role.description}
            </Breakdown.Text>
          )}
          <Breakdown.InfoTable>
            <Breakdown.InfoTable.Row
              title={texts.rolePlayingAgeLabel}
              content={role.playingAge}
              isPrintView={isPrintView}
            />
            <Breakdown.InfoTable.Row
              title={texts.roleHeightLabel}
              content={role.height}
              isPrintView={isPrintView}
            />
            <Breakdown.InfoTable.Row
              title={texts.roleGenderLabel}
              content={role.gender}
              isPrintView={isPrintView}
            />
            {flags[
              FEATURE_FLAGS["HeritageAndEthnicity-BreakdownsAndJobFilters"]
            ] && (
              <Breakdown.InfoTable.Row
                title={t("agent:heritage.ethnicity")}
                content={
                  role.anyEthnicity
                    ? t("agent:heritage.ethnicity.all.ethnicities")
                    : role.ethnicityLabel
                }
                isPrintView={isPrintView}
              />
            )}
            <Breakdown.InfoTable.Row
              title={texts.roleAppearanceLabel}
              content={role.appearance}
              isPrintView={isPrintView}
            />
            <Breakdown.InfoTable.Row
              title={texts.roleContractTypeLabel}
              content={role.contractType}
              isPrintView={isPrintView}
            />
            <Breakdown.InfoTable.Row
              title={texts.roleContractTypeInfoLabel}
              content={role.contractTypeInfo}
              keepFormatting={true}
              isPrintView={isPrintView}
            />
            {Boolean(role.linkAttachments.length) && (
              <Breakdown.InfoTable.Row
                title={texts.roleAttachmentsLabel}
                content={<>{role.linkAttachments.map(this.renderAttachment)}</>}
                isPrintView={isPrintView}
              />
            )}
          </Breakdown.InfoTable>
        </Breakdown.Section>
        {user.type === AccountType.Agent ? (
          <SuggestionsViewAgent {...suggestionsViewAgentProps} />
        ) : (
          <SuggestionsViewPerformer {...suggestionsViewPerformerProps} />
        )}
      </>
    );
  }

  private renderPrintArea() {
    const { breakdown, texts } = this.props;

    return (
      <div className="c-breakdown-view__print-area">
        {this.renderTermsMessage(true)}
        <Breakdown.Title
          title={texts.title}
          sentBy={texts.sent}
          closingDate={texts.closingDate}
          exclusive={false}
          exclusiveText=""
          isPrintView={true}
        />
        {breakdown.message && (
          <Breakdown.Message keepFormatting={true} isPrintView={true}>
            {breakdown.message}
          </Breakdown.Message>
        )}
        {this.renderSummarySection(true)}
        {this.renderProductionDetailsSection(true)}
        {this.renderCastingDetailsSection(true)}
        <Breakdown.SectionTitle isPrintView={true}>
          {texts.rolesSectionTitle}
        </Breakdown.SectionTitle>
        {this.props.roles.map((role, index) => {
          return (
            <div key={role.id}>
              <h3 className="c-breakdown-view__print-role-title">
                {role.title}
              </h3>
              {this.renderRoleContent(role, index, true)}
            </div>
          );
        })}
      </div>
    );
  }

  private handleReportConcern = (roleId: string) => () => {
    const { breakdown, translation, complaintCategories } = this.props;
    const texts = prepareReportConcernPopupTexts(
      translation.common,
      complaintCategories
    );

    this.props.showPopup({
      type: PopupType.ReportConcernPopup,
      data: {
        texts,
        onSubmit: async (model: IReportConcernPopupModel) => {
          try {
            await this.props.reportConcern({
              linkmailId: breakdown.id,
              roleId,
              type: findFacetName(complaintCategories, model.typeId),
              details: model.details,
            });
            this.props.hidePopup();
          } catch (e) {
            console.error(e);
            this.handleError();
          }
        },
        hidePopup: this.props.hidePopup,
      },
    });
  };

  private handleError() {
    this.props.showPopup({
      type: PopupType.ErrorPopup,
      data: {
        ...getErrorPopupTexts()[getErrorPopupType()],
        hidePopup: this.props.hidePopup,
      },
    });
  }

  private collapseRoles = () => {
    this.rolesRowRefs.forEach((row) => row?.collapse());
  };

  private expandRoles = () => {
    this.rolesRowRefs.forEach((row) => row?.expand());
  };

  private hasFullAccess() {
    if (this.props.user.type === AccountType.Agent) {
      return true;
    }

    return (
      this.props.user.performerAccessType === AccessType.Full ||
      this.props.user.performerAccessType === undefined
    );
  }

  private switchEditMode =
    (role: IBreakdownViewRole, roleRowIndex: number) => (isEdit: boolean) => {
      this.setState({
        currentEditingRole: isEdit ? role : undefined,
      });
      scrollManager.scrollToElement(
        // eslint-disable-next-line react/no-find-dom-node
        ReactDOM.findDOMNode(this.rolesRowRefs[roleRowIndex]) as Element,
        0,
        0
      );
    };

  private hasAccessForSuggestion(): boolean {
    const { breakdown } = this.props;

    return (
      dateService.isBeforeNow(breakdown.castingClosingDate) ||
      breakdown.freezeDate !== null
    );
  }

  private registerRole = (data: IBreakdownViewRegisterRole) => {
    this.rolesRegistry.set(data.id, data.callback);
  };

  private handleDataLoaded = (data: IBreakdownViewOnDataLoaded) => {
    this.rolesRegistry.get(data.id)?.(data.loadedData);
  };
}

const mapStateToProps = (
  state: IApplicationState
): IBreakdownViewStoreProps => {
  const userType = state.user.data!.type;

  return {
    translation: state.translation,
    complaintCategories: state.facet[FacetStoreKey.ComplaintCategory]!,
    isPerformer: userType === AccountType.Performer,
  };
};

const mapDispatchToProps: MapDispatchToProps<IBreakdownViewActions> = {
  showPopup,
  hidePopup,
  reportConcern,
};

export default compose(
  withTranslation(),
  withLaunchDarklyConsumerHOC(),
  connect(mapStateToProps, mapDispatchToProps)
)(BreakdownView) as React.ComponentType<
  React.PropsWithChildren<IBreakdownViewComposeProps>
>;
